/**
 * Engagement & Activation — Admin → Adoption sub-tab.
 *
 * The value-moment activation funnel ("aha" = first deep session), time-to-value,
 * the activated-vs-not retention/churn split, and the session-depth histogram.
 * The deep-session threshold is an interactive control that re-runs the funnel
 * client-side instantly (no refetch). Prompt-based only — uses useData() events.
 */
import { useMemo, useState } from 'react';
import { useData } from '../AdoptionData.jsx';
import { companyOf } from '../../../../lib/adoptionAnalytics.js';
import { valueMomentFunnel, sessionDepthHistogram } from '../../../../lib/adoptionSessionAnalytics.js';
import { FunnelBars } from '../charts.jsx';
import { Kpi, Kpis } from '../Kpi.jsx';

/** Inline column chart for the session-depth histogram (percent per bucket). */
function DepthBars({ buckets }) {
  if (!buckets.length) return null;
  const W = Math.max(360, buckets.length * 92), H = 200, m = { t: 16, r: 12, b: 30, l: 28 };
  const pw = W - m.l - m.r, ph = H - m.t - m.b, band = pw / buckets.length, bw = Math.min(60, band * 0.6);
  const max = Math.max(1, ...buckets.map((b) => b.pct));
  const color = (b) => (b.bucket.includes('deep') ? '#0d9488' : '#7fd1c8');
  return (
    <svg className="chart" viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label="Session depth distribution">
      {buckets.map((b, i) => {
        const h = (b.pct / max) * ph, x = m.l + band * i + (band - bw) / 2, y = m.t + ph - h;
        return (
          <g key={b.bucket}>
            <rect x={x} y={y} width={bw} height={h} rx="2" fill={color(b)}><title>{b.bucket}: {b.pct}%</title></rect>
            <text x={x + bw / 2} y={y - 4} textAnchor="middle" fontSize="10" fill="#334">{b.pct}%</text>
            <text x={x + bw / 2} y={H - 10} textAnchor="middle" fontSize="9.5" fill="#778">{b.bucket}</text>
          </g>
        );
      })}
    </svg>
  );
}

export default function EngagementActivation() {
  const { events, aliases } = useData();
  const [scope, setScope] = useState('__ALL__');
  const [deepN, setDeepN] = useState(5);

  const companies = useMemo(
    () => [...new Set(events.map((e) => companyOf(e.user, aliases)))].sort(),
    [events, aliases]
  );
  const ev = useMemo(
    () => (scope === '__ALL__' ? events : events.filter((e) => companyOf(e.user, aliases) === scope)),
    [events, aliases, scope]
  );
  const funnel = useMemo(() => valueMomentFunnel(ev, { deepN }), [ev, deepN]);
  const depth = useMemo(() => sessionDepthHistogram(ev), [ev]);

  if (!ev.length) return <div className="status">No activity in this window.</div>;

  return (
    <>
      <div className="card-head">
        <h1>Engagement &amp; activation</h1>
        <div className="card-controls">
          <label className="adopt-days">Deep session ≥
            <input
              type="number" min="2" max="15" value={deepN}
              onChange={(e) => setDeepN(Math.max(2, Math.min(15, parseInt(e.target.value || '5', 10))))}
              aria-label="Deep-session query threshold"
            /> queries
          </label>
          <select value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value="__ALL__">All companies</option>
            {companies.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <h2 className="section-rule">Activation</h2>
      <Kpis>
        <Kpi v={`${funnel.activationRate}%`} l="Activation rate (reach a deep session)" />
        <Kpi v={funnel.medianDaysToValue == null ? '—' : `${funnel.medianDaysToValue}d`} l="Median time-to-value" />
        <Kpi v={`${funnel.within7Pct}%`} l="Activate within 7 days" />
        <Kpi v={`${funnel.sustainedActivated}% / ${funnel.sustainedNot}%`} l="Sustained: activated / not" />
        <Kpi v={`${funnel.churnActivated}% / ${funnel.churnNot}%`} l="Churn: activated / not" />
      </Kpis>

      <section className="card">
        <h3>Value-moment funnel <span className="muted">— “aha” = first session with ≥{deepN} queries in 30 min</span></h3>
        <FunnelBars stages={funnel.stages} />
      </section>

      <section className="card">
        <p className="muted" style={{ margin: 0 }}>
          Reaching the value moment is the retention lever: activated users sustain{' '}
          <b>{funnel.sustainedActivated}%</b> vs <b>{funnel.sustainedNot}%</b> and churn{' '}
          <b>{funnel.churnActivated}%</b> vs <b>{funnel.churnNot}%</b>. The leak is the gap between
          “ran 2+ queries” and the deep session — the highest-ROI nurture cohort.
        </p>
      </section>

      <h2 className="section-rule">Session depth</h2>
      <Kpis>
        <Kpi v={depth.medianQueries} l="Median queries / session" />
        <Kpi v={`${depth.deepPct}%`} l="Sessions that go deep (5+)" />
        <Kpi v={`${depth.singlePct}%`} l="Single-query sessions" />
      </Kpis>
      <section className="card">
        <h3>How deep does a session go?</h3>
        <DepthBars buckets={depth.buckets} />
      </section>
    </>
  );
}
